import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import CustomButton from "components/common/Button/CustomButton";
import { LoginContext } from "helpers/LoginContext";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  SETTINGS_BREADCRUMB_PAGES,
  SP_SETTING_BREADCRUMB,
} from "Constants/BreadcrumbConstant";
import {
  URL_SP_AI_SETTINGS,
  URL_SP_AI_SETTINGS_UPDATE,
} from "store/services/URL";
import { toast } from "react-toastify";
import { mergeOrgAiIntoSpSettings } from "helpers/spAiFeatureAccess";
import { Tooltip } from "react-tooltip";
import {
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineCreditCard,
  HiOutlineInformationCircle,
  HiOutlineSearch,
  HiOutlineSparkles,
} from "react-icons/hi";
import {
  ACCENT_STYLES,
  MODULE_META,
  childMetaLabelFromSchema,
  dependentChildrenOf,
  getChildBrief,
  getChildSummary,
  isSchemaAvailable,
  moduleSearchText,
  requiredChildFor,
  sortSchemaEntries,
} from "./aiSettingsConfig";
import AiSettingsPageSkeleton from "./AiSettingsSkeleton";

const snapshotState = (enabled, features) =>
  JSON.stringify({ ai_enabled: Boolean(enabled), ai_features: features || {} });

const countEnabledFeatures = (schema, features, masterOn) => {
  if (!masterOn || !schema) {
    return { modules: 0, children: 0, totalModules: 0, totalChildren: 0 };
  }
  let modules = 0;
  let children = 0;
  let totalModules = 0;
  let totalChildren = 0;
  Object.entries(schema).forEach(([moduleKey, meta]) => {
    if (!isSchemaAvailable(meta)) return;
    totalModules += 1;
    const node = features?.[moduleKey] || {};
    if (node.enabled) modules += 1;
    Object.entries(meta?.children || {}).forEach(([childKey, childMeta]) => {
      if (!isSchemaAvailable(childMeta)) return;
      totalChildren += 1;
      if (node[childKey]) children += 1;
    });
  });
  return { modules, children, totalModules, totalChildren };
};

const AiSettings = () => {
  const { sideBarPadding } = useContext(LoginContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [schema, setSchema] = useState({});
  const [aiFeatures, setAiFeatures] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [search, setSearch] = useState("");
  const [expandedModules, setExpandedModules] = useState({});
  const [showLayersHelp, setShowLayersHelp] = useState(false);

  const hydrateFromPayload = useCallback((payload) => {
    const enabled = Boolean(payload?.ai_enabled);
    const features = payload?.ai_features || {};
    setAiEnabled(enabled);
    setSchema(payload?.schema || {});
    setAiFeatures(features);
    setSavedSnapshot(snapshotState(enabled, features));
    mergeOrgAiIntoSpSettings({
      ai_enabled: payload?.ai_enabled,
      ai_org_permissions: payload?.ai_org_permissions,
    });
    const moduleKeys = Object.keys(payload?.schema || {});
    const firstAvailable = moduleKeys.find((key) =>
      isSchemaAvailable(payload?.schema?.[key])
    );
    setExpandedModules(
      moduleKeys.reduce((acc, key) => {
        acc[key] = key === firstAvailable;
        return acc;
      }, {})
    );
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiGetRequest(URL_SP_AI_SETTINGS);
      const payload = res?.data?.data ?? res?.data;
      hydrateFromPayload(payload || {});
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not load AI settings."
      );
    } finally {
      setLoading(false);
    }
  }, [hydrateFromPayload]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const isDirty = useMemo(
    () => snapshotState(aiEnabled, aiFeatures) !== savedSnapshot,
    [aiEnabled, aiFeatures, savedSnapshot]
  );

  const stats = useMemo(
    () => countEnabledFeatures(schema, aiFeatures, aiEnabled),
    [schema, aiFeatures, aiEnabled]
  );

  const filteredModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    const entries = Object.entries(schema || {}).filter(([moduleKey, moduleMeta]) => {
      if (!q) return true;
      const haystack = moduleSearchText(moduleKey, moduleMeta).toLowerCase();
      return haystack.includes(q) || moduleKey.toLowerCase().includes(q);
    });
    return sortSchemaEntries(entries);
  }, [schema, search]);

  const setModuleValue = useCallback((moduleKey, nextNode) => {
    setAiFeatures((prev) => ({
      ...prev,
      [moduleKey]: nextNode,
    }));
  }, []);

  const handleModuleToggle = useCallback(
    (moduleKey, enabled) => {
      if (!isSchemaAvailable(schema?.[moduleKey])) return;
      const children = Object.entries(schema?.[moduleKey]?.children || {});
      const currentNode = aiFeatures?.[moduleKey] || {};
      const nextNode = { ...currentNode, enabled };
      if (!enabled) {
        children.forEach(([child]) => {
          nextNode[child] = false;
        });
      }
      setModuleValue(moduleKey, nextNode);
      if (enabled) {
        setExpandedModules((prev) => ({ ...prev, [moduleKey]: true }));
      }
    },
    [aiFeatures, schema, setModuleValue]
  );

  const handleChildToggle = useCallback(
    (moduleKey, childKey, childEnabled) => {
      const childMeta = schema?.[moduleKey]?.children?.[childKey];
      if (!isSchemaAvailable(childMeta)) return;
      const currentNode = aiFeatures?.[moduleKey] || {};
      const children = Object.keys(schema?.[moduleKey]?.children || {});
      const required = requiredChildFor(moduleKey, childKey);
      if (childEnabled && required && !currentNode[required]) return;
      const nextNode = { ...currentNode, [childKey]: childEnabled };
      if (!childEnabled) {
        dependentChildrenOf(moduleKey, childKey).forEach((dependent) => {
          nextNode[dependent] = false;
        });
      }
      if (moduleKey === "ai_care_watch") {
        if (childEnabled) nextNode.enabled = true;
        setModuleValue(moduleKey, nextNode);
        return;
      }
      if (childEnabled) {
        nextNode.enabled = true;
      } else if (children.length > 0) {
        const hasAnyEnabledChild = children.some((child) =>
          child === childKey ? false : Boolean(nextNode[child])
        );
        if (!hasAnyEnabledChild) {
          nextNode.enabled = false;
        }
      }
      setModuleValue(moduleKey, nextNode);
    },
    [aiFeatures, schema, setModuleValue]
  );

  const handleEnableAllModules = () => {
    if (!aiEnabled) return;
    const next = { ...aiFeatures };
    Object.entries(schema || {}).forEach(([moduleKey, moduleMeta]) => {
      if (!isSchemaAvailable(moduleMeta)) return;
      const node = { ...(next[moduleKey] || {}), enabled: true };
      Object.entries(moduleMeta?.children || {}).forEach(([child, childMeta]) => {
        if (isSchemaAvailable(childMeta)) {
          node[child] = true;
        }
      });
      next[moduleKey] = node;
    });
    setAiFeatures(next);
  };

  const handleDisableAllModules = () => {
    const next = { ...aiFeatures };
    Object.entries(schema || {}).forEach(([moduleKey, moduleMeta]) => {
      const node = { ...(next[moduleKey] || {}), enabled: false };
      Object.keys(moduleMeta?.children || {}).forEach((child) => {
        node[child] = false;
      });
      next[moduleKey] = node;
    });
    setAiFeatures(next);
  };

  const handleDiscard = () => {
    try {
      const parsed = JSON.parse(savedSnapshot);
      setAiEnabled(Boolean(parsed.ai_enabled));
      setAiFeatures(parsed.ai_features || {});
    } catch {
      fetchSettings();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await apiPostRequest(URL_SP_AI_SETTINGS_UPDATE, {
        ai_enabled: aiEnabled,
        ai_features: aiFeatures,
      });
      const updatedPayload = res?.data?.data ?? res?.data;
      hydrateFromPayload(updatedPayload || {});
      toast.success("AI settings saved.");
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to save AI settings.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (moduleKey) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  };

  return (
    <div className="min-h-screen bg-[#F6F7F8]">
      <SideBar />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col min-h-screen`}
      >
        <Header
          title="AI Settings"
          breadcrumb={
            <Breadcrumb pages={SP_SETTING_BREADCRUMB(SETTINGS_BREADCRUMB_PAGES.AI_SETTINGS)} />
          }
        />

        <section className="container mx-auto sm:px-6 lg:px-8 mt-3 pb-28 flex-1">
          {loading ? (
            <AiSettingsPageSkeleton />
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                  <HiOutlineInformationCircle className="h-5 w-5 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Master control + quick stats */}
              <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
                <div className="lg:col-span-2 rounded-xl border border-blue-200 bg-gradient p-6 text-white shadow-md">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-blue-100 flex items-center gap-2">
                        <HiOutlineSparkles className="h-4 w-4 shrink-0" />
                        Organisation-wide AI
                      </p>
                      <h2 className="text-2xl font-bold tracking-tight mt-1">
                        Enable AI for this organisation
                      </h2>
                      <p className="mt-2 text-sm text-blue-100 max-w-xl leading-relaxed">
                        When turned off, every AI feature is blocked for all users —
                        regardless of role permissions. Turn on individual modules
                        below when you are ready.
                      </p>
                      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-medium">
                        <span
                          className={`mb-0.5 h-2 w-2 rounded-full ${
                            aiEnabled ? "bg-emerald-300" : "bg-red-300"
                          }`}
                        />
                        {aiEnabled ? "AI is active" : "AI is fully disabled"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <SwitchWithIcon
                        checked={aiEnabled}
                        onChange={setAiEnabled}
                        disabled={saving}
                        cssClass="bg-white/20"
                      />
                      <span className="text-xs text-white">Master switch</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex flex-col gap-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    At a glance
                  </p>
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <div className="rounded-lg bg-[#ECF0FD] px-3 py-3 border border-blue-100">
                      <p className="text-xs text-[#4B6CB7]">Modules on</p>
                      <p className="text-2xl font-bold text-[#02397A] mt-0.5">
                        {aiEnabled ? stats.modules : 0}
                        <span className="text-sm font-normal text-blue-1000">
                          /{stats.totalModules}
                        </span>
                      </p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-3 py-3 border border-gray-100">
                      <p className="text-xs text-gray-500">Sub-features on</p>
                      <p className="text-2xl font-bold text-gray-900 mt-0.5">
                        {aiEnabled ? stats.children : 0}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/settings/ai-usage")}
                    className="flex items-center gap-2 text-sm font-medium text-[#4B6CB7] hover:text-[#293F6A] transition-colors"
                  >
                    <HiOutlineCreditCard className="h-4 w-4" />
                    View credits &amp; usage
                  </button>
                </div>
              </div>

              {/* How it works */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowLayersHelp((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#ECF0FD] text-[#4B6CB7]">
                      <HiOutlineInformationCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        How access layers work
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Organisation → feature module → user role permission
                      </p>
                    </div>
                  </div>
                  {showLayersHelp ? (
                    <HiOutlineChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <HiOutlineChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {showLayersHelp && (
                  <div className="px-5 pb-5 border-t border-gray-100 bg-gray-50/50">
                    <ol className="mt-4 space-y-3 text-sm text-gray-600">
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full background-gradient text-xs font-bold text-white">
                          1
                        </span>
                        <span>
                          <strong className="text-gray-900">This page</strong> — you
                          control which AI capabilities your organisation allows.
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full background-gradient text-xs font-bold text-white">
                          2
                        </span>
                        <span>
                          <strong className="text-gray-900">Role permissions</strong> —
                          in Roles &amp; Permissions, you decide which staff can use each
                          AI tool.
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full background-gradient text-xs font-bold text-white">
                          3
                        </span>
                        <span>
                          <strong className="text-gray-900">Credits</strong> — usage is
                          metered; an empty wallet blocks AI even when both layers above
                          allow access.
                        </span>
                      </li>
                    </ol>
                  </div>
                )}
              </div>

              {/* Feature modules */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      AI feature modules
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Choose what your organisation can use. Additional modules are
                      listed below and will be configurable when they launch.
                    </p>
                  </div>
                  {aiEnabled && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleEnableAllModules}
                        disabled={saving}
                        className="text-xs font-medium text-[#4B6CB7] hover:underline disabled:opacity-50"
                      >
                        Enable all available
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={handleDisableAllModules}
                        disabled={saving}
                        className="text-xs font-medium text-gray-600 hover:underline disabled:opacity-50"
                      >
                        Disable all
                      </button>
                    </div>
                  )}
                </div>

                <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                  <div className="relative max-w-md">
                    <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search features…"
                      disabled={!aiEnabled}
                      className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {!aiEnabled && (
                  <div className="mx-4 sm:mx-5 my-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    Organisation AI is off. Turn on the master switch above to configure
                    individual modules.
                  </div>
                )}

                <div className="divide-y divide-gray-100">
                  {filteredModules.length === 0 ? (
                    <div className="px-5 py-12 text-center text-sm text-gray-500">
                      No features match your search.
                    </div>
                  ) : (
                    filteredModules.map(([moduleKey, moduleMeta]) => {
                      const node = aiFeatures?.[moduleKey] || {};
                      const moduleEnabled = Boolean(node?.enabled);
                      const moduleAvailable = isSchemaAvailable(moduleMeta);
                      const children = sortSchemaEntries(
                        Object.entries(moduleMeta?.children || {})
                      );
                      const uiMeta = MODULE_META[moduleKey] || {};
                      const accent = ACCENT_STYLES[uiMeta.accent] || ACCENT_STYLES.gray;
                      const Icon = uiMeta.icon || HiOutlineSparkles;
                      const expanded = expandedModules[moduleKey];
                      const availableChildren = children.filter(([, childMeta]) =>
                        isSchemaAvailable(childMeta)
                      );
                      const activeChildCount = availableChildren.filter(([ck]) =>
                        Boolean(node?.[ck])
                      ).length;

                      return (
                        <div
                          key={moduleKey}
                          className={`transition-opacity ${
                            aiEnabled && moduleAvailable ? "opacity-100" : "opacity-70"
                          }`}
                        >
                          <div className="px-4 sm:px-5 py-4 flex items-start gap-4">
                            <div
                              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${accent.iconBg} ${accent.text}`}
                            >
                              <Icon className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <button
                                  type="button"
                                  onClick={() => toggleExpanded(moduleKey)}
                                  className="text-left group min-w-0"
                                >
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-base font-semibold text-gray-900 group-hover:text-[#4B6CB7] transition-colors">
                                      {moduleMeta?.label}
                                    </p>
                                    {children.length > 0 && (
                                      <span className="text-gray-400">
                                        {expanded ? (
                                          <HiOutlineChevronDown className="h-4 w-4 inline" />
                                        ) : (
                                          <HiOutlineChevronRight className="h-4 w-4 inline" />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                    {uiMeta.description ||
                                      "Configure AI capabilities for this area."}
                                  </p>
                                  {moduleEnabled && availableChildren.length > 0 && (
                                    <p className="text-xs text-[#4B6CB7] mt-1.5 font-medium">
                                      {activeChildCount} of {availableChildren.length}{" "}
                                      sub-features enabled
                                    </p>
                                  )}
                                </button>

                                <div className="flex items-center gap-3 shrink-0">
                                  <span
                                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                      !moduleAvailable
                                        ? "bg-gray-100 text-gray-500"
                                        : moduleEnabled && aiEnabled
                                          ? `${accent.bg} ${accent.text}`
                                          : "bg-gray-100 text-gray-500"
                                    }`}
                                  >
                                    {!moduleAvailable
                                      ? "Coming soon"
                                      : moduleEnabled && aiEnabled
                                        ? "On"
                                        : "Off"}
                                  </span>
                                  <SwitchWithIcon
                                    checked={moduleEnabled}
                                    onChange={(val) => handleModuleToggle(moduleKey, val)}
                                    disabled={saving || !aiEnabled || !moduleAvailable}
                                  />
                                </div>
                              </div>
                              {expanded && children.length > 0 && (
                                <div className="mt-4 space-y-2 pl-0 sm:pl-1">
                                  {children.map(([childKey, childMeta]) => {
                                    const label = childMetaLabelFromSchema(childMeta);
                                    const childAvailable = isSchemaAvailable(childMeta);
                                    const brief = getChildBrief(
                                      moduleKey,
                                      childKey,
                                      label
                                    );
                                    const summary = getChildSummary(
                                      moduleKey,
                                      childKey,
                                      brief
                                    );
                                    const tooltipId = `ai-feature-${moduleKey}-${childKey}`;
                                    const childOn = Boolean(node?.[childKey]);
                                    const requiredKey = requiredChildFor(moduleKey, childKey);
                                    const blockedByRequired =
                                      Boolean(requiredKey) && !node?.[requiredKey];
                                    const requiredLabel = requiredKey
                                      ? childMetaLabelFromSchema(
                                          moduleMeta?.children?.[requiredKey]
                                        )
                                      : "";
                                    return (
                                      <div
                                        key={childKey}
                                        className={`flex items-start justify-between gap-4 rounded-lg border px-4 py-3 transition-colors ${
                                          childOn && moduleEnabled && aiEnabled && childAvailable
                                            ? `border-blue-200 ${accent.bg}`
                                            : "border-gray-100 bg-gray-50/80"
                                        } ${!childAvailable ? "opacity-75" : ""}`}
                                      >
                                        <div className="min-w-0 pr-2">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <p className="text-sm font-medium text-gray-900">
                                              {label}
                                            </p>
                                            <button
                                              type="button"
                                              className="shrink-0 text-gray-400 hover:text-[#4B6CB7] focus:outline-none focus-visible:text-[#4B6CB7]"
                                              data-tooltip-id={tooltipId}
                                              aria-label={`More about ${label}`}
                                            >
                                              <HiOutlineInformationCircle className="h-4 w-4" />
                                            </button>
                                            <Tooltip
                                              id={tooltipId}
                                              place="top"
                                              variant="dark"
                                              className="z-[501] !max-w-xs !text-xs !leading-relaxed"
                                              content={summary}
                                            />
                                          </div>
                                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                            {brief}
                                          </p>
                                          {blockedByRequired && childAvailable ? (
                                            <p className="text-xs text-[#B45309] mt-1 font-medium">
                                              {`Turn on ${requiredLabel || "the required feature"} first.`}
                                            </p>
                                          ) : null}
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                          <span
                                            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                              !childAvailable
                                                ? "bg-gray-100 text-gray-500"
                                                : childOn && moduleEnabled && aiEnabled
                                                  ? `${accent.bg} ${accent.text}`
                                                  : "bg-gray-100 text-gray-500"
                                            }`}
                                          >
                                            {!childAvailable
                                              ? "Coming soon"
                                              : childOn && moduleEnabled && aiEnabled
                                                ? "On"
                                                : "Off"}
                                          </span>
                                          <SwitchWithIcon
                                            checked={childOn}
                                            onChange={(val) =>
                                              handleChildToggle(moduleKey, childKey, val)
                                            }
                                            disabled={
                                              saving ||
                                              !aiEnabled ||
                                              !moduleEnabled ||
                                              !childAvailable ||
                                              (blockedByRequired && !childOn)
                                            }
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {expanded && children.length === 0 && (
                                <p className="mt-3 text-sm text-gray-500 italic">
                                  {moduleAvailable
                                    ? "This module has no separate sub-toggles — the module switch controls access."
                                    : "This module is not available yet. It will appear here when launched."}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Sticky save bar */}
        {!loading && (
          <div
            className={`fixed bottom-0 right-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.06)] transition-[padding] duration-300 ${
              sideBarPadding ? "left-0 lg:left-[4rem]" : "left-0 lg:left-64"
            }`}
          >
            <div className="container mx-auto sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm">
                {isDirty ? (
                  <span className="text-amber-700 font-medium flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                ) : (
                  <span className="text-gray-500">All changes saved</span>
                )}
              </div>
              <div className="flex items-center gap-2 justify-end">
                <CustomButton
                  label="Discard"
                  clickHandler={handleDiscard}
                  isDisabled={saving || !isDirty}
                  variant="secondary"
                  cssClass="min-w-[5.5rem] justify-center"
                />
                <CustomButton
                  label={saving ? "Saving…" : "Save settings"}
                  clickHandler={handleSave}
                  isDisabled={saving || !isDirty}
                  showLoading={saving}
                  loaderCssClass="loader-xs border-white"
                  variant="primary"
                  cssClass="min-w-[9rem] justify-center"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiSettings;
